import React, {useEffect, useState} from 'react';
import Container from "../../components/UI/Container/Container";
import Block from "../../components/UI/Block/Block";
import {
  Avatar,
  Button,
  Col,
  Flex,
  Form, Input,
  InputNumber,
  List,
  message,
  Modal,
  Row,
  Skeleton,
  Spin,
  Typography
} from "antd";
import {EuroOutlined, PercentageOutlined, PlusOutlined} from "@ant-design/icons";
import {API} from "../../http";
import {NEW_ENDPOINTS} from "../../http/endpoints";
import logo from '../../assets/svg/logo_icon.svg';
import {FIELDS_SCHEME, GENERAL_SCHEME} from "../../schemas";

const IconText = ({icon, text}) => (
  <>
    {React.createElement(icon, {style: {marginRight: 8}})}
    {text}
  </>
);

const Tariffs = () => {
  const [loading, setLoading] = useState(false);
  const [tariffs, setTariffs] = useState([]);

  const [form] = Form.useForm();

  const onAddNewClick = async () => {
    const modal = Modal.info({
      title: "Add new tariff",
      maskClosable: true,
      closable: true,
      okText: 'Confirm',
      okButtonProps: { type: 'primary', size: 'large' },
      width: 500,
      onCancel: () => {
        form.resetFields();
      },
      onOk: async () => {
        try {
          const values = await form.validateFields();
          setLoading(true);
          const {data} = await API.post(NEW_ENDPOINTS.ADMIN.TARIFFS, values, {
            headers: {
              token: 'my1perfect2token',
            }
          });
          setTariffs(prev => [...prev, data]);
          form.resetFields();
          modal.destroy();
          message.success('New tariff added successfully');
        } catch (e) {
          message.error('Please fill in all required fields');
          console.error(e);
        } finally {
          setLoading(false);
        }
      },
      content: (
        <Form labelAlign="left" labelCol={{ xs: 24 }} size="large" variant="filled" form={form} layout="horizontal">
          <Typography.Paragraph>
            Please fill in the following fields to add a new tariff plan
          </Typography.Paragraph>
          <Row gutter={[16, 10]}>
            <Col xs={24}>
              <Form.Item
                name="name"
                label="Tariff name"
                style={{ marginBottom: 0 }}
                rules={[...GENERAL_SCHEME.required, FIELDS_SCHEME.name]}
              >
                <Input placeholder="Tariff name" style={{width: '100%'}}/>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="sepa"
                label="SEPA"
                style={{ marginBottom: 0 }}
                rules={GENERAL_SCHEME.required}
              >
                <InputNumber placeholder="SEPA" min={0} precision={0} style={{width: '100%'}}/>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="swift"
                label="SWIFT"
                style={{ marginBottom: 0 }}
                rules={GENERAL_SCHEME.required}
              >
                <InputNumber placeholder="SWIFT" min={0} precision={0} style={{width: '100%'}}/>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="monthly"
                label="Monthly"
                style={{ marginBottom: 0 }}
                rules={GENERAL_SCHEME.required}
              >
                <InputNumber placeholder="Monthly" min={0} precision={0} style={{width: '100%'}}/>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="percent"
                label="Percent"
                style={{ marginBottom: 0 }}
                rules={GENERAL_SCHEME.required}
              >
                <InputNumber placeholder="Percent" min={0} precision={1} max={100} step={1} style={{width: '100%'}}/>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      ),
    })
  }

  useEffect(() => {
    const fetchTariffs = async () => {
      setLoading(true);
      try {
        const {data} = await API.get(NEW_ENDPOINTS.ADMIN.TARIFFS, {
          headers: {
            token: 'my1perfect2token',
          }
        });
        setTariffs(data);
      } catch (e) {
        message.error('Error fetching tariffs list');
        setTariffs([]);
      } finally {
        setLoading(false);
      }
    }

    fetchTariffs();
  }, []);

  return (
    <Container>
      <Spin tip="Loading..." spinning={loading}>
        <Block>
          <Flex>
            <Typography.Title style={{flexGrow: 1}} level={2}>Available tariffs</Typography.Title>
            <Button onClick={onAddNewClick} type="primary" size="large" icon={<PlusOutlined/>} shape="round">Add
              new</Button>
          </Flex>
          <List
            size="large"
            itemLayout="vertical"
            dataSource={tariffs}
            renderItem={(item) => (
              <List.Item
                key={item.id}
                actions={
                  !loading
                    ? [
                      <IconText icon={EuroOutlined} text={`SEPA: ${item.sepa}`} key="tariff-sepa-value"/>,
                      <IconText icon={EuroOutlined} text={`SWIFT: ${item.swift}`} key="tariff-swift-value"/>,
                      <IconText icon={EuroOutlined} text={`Monthly: ${item.monthly}`} key="tariff-monthly-value"/>,
                      <IconText icon={PercentageOutlined} text={`Percent: ${item.percent}`} key="tariff-percent-value"/>,
                    ]
                    : undefined
                }
              >
                <Skeleton loading={loading} active avatar>
                  <List.Item.Meta
                    avatar={<Avatar size={50} src={logo}/>}
                    title={item?.name || 'Unknown Tariff'}
                    description={item.id}
                  />
                  {item.content}
                </Skeleton>
              </List.Item>
            )}
          />
        </Block>
      </Spin>
    </Container>
  );
};

export default Tariffs;
